// ── Theme registry ───────────────────────────────────────────────────────────
// One config per theme, same schema for all 15. A theme is pure presentation: it never touches
// game rules or state. Artwork lives under public/themes/<id>/ (and public/themes/_shared/ for
// the shared deck); every image path here is optional at runtime — the loader checks each file
// and falls back to the theme's built-in look until the real artwork is supplied.

export type ThemeStatus = "ready" | "planned";
export type CourtKey = `${"J" | "Q" | "K"}${"S" | "H" | "D" | "C"}`;

export interface ThemeConfig {
  id: string;
  displayName: string;
  description: string;
  status: ThemeStatus;            // "planned" themes are configured but hidden from the picker
  cssClass: string;               // body class carrying the theme's CSS (card skin, panels, buttons…)
  previewImage?: string;          // 640×400 picker thumbnail
  backgroundImage?: { desktop: string; tablet: string; mobile: string }; // 16:9, 4:3, 9:16 crops
  backgroundDim?: number;         // 0–1 dark overlay over the background so the table stays readable
  boardTexture?: string;          // seamless felt tile
  boardFrame?: string;            // seamless rail texture (wood/metal) — the oval shape itself is CSS
  cardFaceStyle: "standard";      // all themes share the standard deck for now
  courtArt?: Partial<Record<CourtKey, string>>; // optional themed J/Q/K — overrides the shared set
  cardBackImage?: string;         // 5:7, symmetrical / reversible
  mapBackground?: string;         // 16:10 map paper (no coastlines — the states are drawn on top)
  lobbyImage?: string;            // title sign for the lobby/setup screen (transparent PNG)
  lobbyFullImage?: string;        // the same sign at full size, for the click-to-enlarge view
  panelTexture?: string;          // seamless texture for score sheet / name plates
  primaryColour: string;
  secondaryColour: string;
  accentColour: string;
  textColour: string;
  panelStyle: "dark" | "leather" | "brass" | "parchment";
  buttonStyle: "outline" | "wood-sign" | "brass" | "neon";
  soundPack?: { deal?: string; knock?: string; win?: string };
  animationStyle: "subtle" | "lively";
  mapDecor?: "frontier" | "sea" | "river";
}

// The shared, traditional deck: court-card illustrations used by every theme unless a theme
// provides its own courtArt.
export const SHARED_COURT: Record<CourtKey, string> = Object.fromEntries(
  (["J", "Q", "K"] as const).flatMap((r) => (["S", "H", "D", "C"] as const).map((s) => [`${r}${s}`, `themes/_shared/cards/court-${r}${s}.webp`])),
) as Record<CourtKey, string>;

const art = (id: string, extra: Partial<ThemeConfig> = {}): Partial<ThemeConfig> => ({
  previewImage: `themes/${id}/preview.webp`,
  backgroundImage: { desktop: `themes/${id}/bg-desktop.webp`, tablet: `themes/${id}/bg-tablet.webp`, mobile: `themes/${id}/bg-mobile.webp` },
  boardTexture: `themes/${id}/board-felt.webp`,
  boardFrame: `themes/${id}/board-rail.webp`,
  cardBackImage: `themes/${id}/card-back.webp`,
  mapBackground: `themes/${id}/map-paper.webp`,
  lobbyImage: `themes/${id}/lobby-sign.webp`,
  lobbyFullImage: `themes/${id}/lobby-full.webp`,
  panelTexture: `themes/${id}/panel.webp`,
  ...extra,
});

export const THEMES: ThemeConfig[] = [
  {
    id: "classic", displayName: "Classic", description: "The original Poli Poker table — green felt, clean white cards.",
    status: "ready", cssClass: "skin-classic", cardFaceStyle: "standard",
    primaryColour: "#2b7d54", secondaryColour: "#1f1c1a", accentColour: "#c28d41", textColour: "#f2ece4",
    panelStyle: "dark", buttonStyle: "outline", animationStyle: "subtle",
  },
  {
    id: "wild-west-saloon", displayName: "Wild West Saloon",
    description: "Worn green felt, dark oak and brass under warm lantern light — a frontier saloon at midnight.",
    status: "ready", cssClass: "skin-saloon", cardFaceStyle: "standard", backgroundDim: 0.45,
    primaryColour: "#3b6338", secondaryColour: "#3e2512", accentColour: "#c9a24a", textColour: "#f3e2bd",
    panelStyle: "leather", buttonStyle: "wood-sign", animationStyle: "subtle", mapDecor: "frontier",
    ...art("wild-west-saloon"),
  },
  {
    id: "frontier-outpost", displayName: "Frontier Outpost",
    description: "An 1890s stencilled deck on a rough trading-post table.",
    status: "ready", cssClass: "skin-frontier", cardFaceStyle: "standard", backgroundDim: 0.45,
    primaryColour: "#4f7d4a", secondaryColour: "#1c2238", accentColour: "#e2b33c", textColour: "#f3e6c8",
    panelStyle: "leather", buttonStyle: "wood-sign", animationStyle: "subtle", mapDecor: "frontier",
    ...art("frontier-outpost"),
  },
  {
    id: "mississippi-riverboat", displayName: "Riverboat Casino",
    description: "Deep burgundy felt, polished mahogany and brass on a paddle-steamer casino.",
    status: "ready", cssClass: "skin-riverboat", cardFaceStyle: "standard", backgroundDim: 0.45,
    primaryColour: "#5e1f27", secondaryColour: "#13233f", accentColour: "#d4af5a", textColour: "#f0dfae",
    panelStyle: "brass", buttonStyle: "brass", animationStyle: "subtle", mapDecor: "river",
    ...art("mississippi-riverboat"),
  },
  {
    id: "roaring-twenties", displayName: "Roaring Twenties",
    description: "Art-deco speakeasy: black lacquer, gold fans and jazz-age glamour.",
    status: "ready", cssClass: "skin-twenties", cardFaceStyle: "standard", backgroundDim: 0.5,
    primaryColour: "#0f3b2e", secondaryColour: "#111111", accentColour: "#d4af37", textColour: "#f3e6c4",
    panelStyle: "brass", buttonStyle: "brass", animationStyle: "subtle",
    ...art("roaring-twenties"),
  },
  {
    id: "orient-express", displayName: "Orient Express",
    description: "Sapphire velvet, walnut marquetry and frosted glass in a 1930s luxury salon car.",
    status: "ready", cssClass: "skin-orient", cardFaceStyle: "standard", backgroundDim: 0.45,
    primaryColour: "#1e3566", secondaryColour: "#3b1f2b", accentColour: "#c9a96e", textColour: "#f1e6cf",
    panelStyle: "brass", buttonStyle: "brass", animationStyle: "subtle",
    ...art("orient-express"),
  },
  {
    id: "las-vegas-high-roller", displayName: "Las Vegas High Roller",
    description: "Emerald felt, black leather and gold in a private high-limit room above the Strip.",
    status: "ready", cssClass: "skin-vegas", cardFaceStyle: "standard", backgroundDim: 0.45,
    primaryColour: "#0f5a3a", secondaryColour: "#0b0b0d", accentColour: "#d4af37", textColour: "#f4e7c2",
    panelStyle: "brass", buttonStyle: "brass", animationStyle: "subtle",
    ...art("las-vegas-high-roller"),
  },
  // ── Planned (Phase 6): configured now so the architecture is proven; hidden until art lands ──
  ...([
    ["medieval-tavern", "Medieval Tavern", "Dark carved timber, iron fittings and candle smoke in a castle tavern.", "#3a2a1a", "#9a7b4f"],
    ["pirates-cove", "Pirate's Cove", "A captain's cabin table, sea charts and treasure maps by lamplight.", "#2a1f14", "#c7a14a"],
    ["cyberpunk-2099", "Cyberpunk 2099", "Dark glass surface, neon cyan borders and humming circuitry.", "#05060a", "#00e5ff"],
    ["samurai-palace", "Samurai Palace", "Lacquered wood, tatami and ink-wash mountains in a Japanese palace.", "#2b1b17", "#c0392b"],
    ["pharaohs-palace", "Pharaoh's Palace", "Sandstone, lapis and gold in the halls of a Pharaoh.", "#1d2b4f", "#d4a017"],
    ["victorian-club", "Victorian Gentlemen's Club", "Green baize, oxblood leather and brass in a London club.", "#1f3b2c", "#b08d57"],
    ["viking-longhouse", "Viking Longhouse", "Hewn oak, furs and firelight in a Norse longhouse.", "#2a2118", "#a8a29e"],
    ["gothic-castle", "Gothic Castle", "Black stone, stained glass and candelabra in a gothic hall.", "#141018", "#8e1b2c"],
    ["galactic-casino", "Galactic Casino", "A space-station casino overlooking a ringed planet.", "#070b1c", "#7c5cff"],
  ] as const).map(([id, displayName, description, secondaryColour, accentColour]): ThemeConfig => ({
    id, displayName, description, status: "planned", cssClass: `theme-${id}`, cardFaceStyle: "standard",
    primaryColour: secondaryColour, secondaryColour, accentColour, textColour: "#f2ece4",
    panelStyle: "dark", buttonStyle: "outline", animationStyle: "subtle", ...art(id),
  })),
];

export const DEFAULT_THEME = "classic";
// Pre-registry saves used short skin names.
export const LEGACY_IDS: Record<string, string> = {
  saloon: "wild-west-saloon", frontier: "frontier-outpost", riverboat: "mississippi-riverboat", classic: "classic",
};
export const themeById = (id: string): ThemeConfig | undefined => THEMES.find((t) => t.id === id);
export const readyThemes = (): ThemeConfig[] => THEMES.filter((t) => t.status === "ready");
